import {
  ChangeDetectorRef,
  Component,
  DestroyRef,
  inject,
  Input,
  ChangeDetectionStrategy,
} from '@angular/core';
import { Language, Post } from 'src/app/core/models/core.models';
import {
  createSynopsis,
  deleteSynopsis,
  generateAISynopsis,
  updateSynopsis,
} from 'src/app/core/actions/post.actions';
import {
  selectPostById,
  selectPostsSynopsisLoading,
} from 'src/app/core/reducers';

import { AppState } from 'src/app/reducers';
import { FormGroup } from '@angular/forms';
import { FormProperty } from 'src/app/core/models/form.models';
import { FormsService } from 'src/app/core/services/forms.service';
import { NomLanguages } from 'src/app/core/constants';
import { Observable } from 'rxjs';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { faSpinner } from '@fortawesome/free-solid-svg-icons';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Component({
  selector: 'app-post-synopsis',
  templateUrl: './post-synopsis.component.html',
  styleUrls: ['./post-synopsis.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class PostSynopsisComponent {
  @Input() postId: string;

  post$: Observable<Post>;
  postSynopsisLoading$: Observable<boolean>;
  post: Post;
  faSpinner = faSpinner;
  currentLanguage: Language = {
    name: 'English',
    id: NomLanguages.ENGLISH,
    code: 'en',
    rtl: false,
  };
  hasSynopsisLanguage: boolean = false;
  hasTranslationLangauge: boolean = false;

  private destroyRef = inject(DestroyRef);

  formGroup: FormGroup;
  formProperties: FormProperty[] = [
    {
      propertyName: 'language_picker',
      controlType: 'language-picker',
      additionalConfig: {
        hasDefault: false,
      },
    },
    {
      propertyName: 'title',
      propertyNameLanguages: {
        [NomLanguages.BULGARIAN]:
          'synopsis.' + NomLanguages.BULGARIAN + '.title',
        [NomLanguages.ENGLISH]: 'synopsis.' + NomLanguages.ENGLISH + '.title',
        [NomLanguages.ARABIC]: 'synopsis.' + NomLanguages.ARABIC + '.title',
      },
      propertyLabel: this.translateService.instant(
        'post-sypnosis.label.sypnosis-title',
      ),
      controlType: 'text',
      additionalConfig: {
        placeholder: this.translateService.instant(
          'post-sypnosis.placeholder.sypnosis-title',
        ),
      },
    },
    {
      propertyName: 'synopsis',
      propertyLabel: this.translateService.instant(
        'post-sypnosis.label.sypnosis',
      ),
      controlType: 'textarea',
      propertyNameLanguages: {
        [NomLanguages.BULGARIAN]:
          'synopsis.' + NomLanguages.BULGARIAN + '.synopsis',
        [NomLanguages.ENGLISH]:
          'synopsis.' + NomLanguages.ENGLISH + '.synopsis',
        [NomLanguages.ARABIC]: 'synopsis.' + NomLanguages.ARABIC + '.synopsis',
      },
      additionalConfig: {
        placeholder: this.translateService.instant(
          'post-sypnosis.placeholder.sypnosis',
        ),
        rows: 20,
      },
    },
  ];

  constructor(
    private formService: FormsService,
    private store: Store<AppState>,
    private cdr: ChangeDetectorRef,
    private translateService: TranslateService,
  ) {
    this.formGroup = this.formService.createFormGroup(this.formProperties);
  }

  ngOnInit() {
    this.post$ = this.store.select(selectPostById(this.postId));
    this.postSynopsisLoading$ = this.store.select(
      selectPostsSynopsisLoading(this.postId),
    );
    this.post$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((post) => {
      this.post = post;
      this.formService.populateFormValues(this.formGroup, post);
      this.estimateHasSynopsisLanguage();
    });
  }

  estimateHasSynopsisLanguage() {
    if (!this.post) return;

    this.hasSynopsisLanguage =
      !!this.post.synopsis &&
      this.currentLanguage.id in this.post.synopsis &&
      this.isValidObject(this.post.synopsis[this.currentLanguage.id]);
    this.cdr.detectChanges();
  }

  isValidObject(obj: any): boolean {
    return obj && Object.keys(obj).length > 0;
  }

  get currentSynopsisId() {
    return this.post.synopsis[this.currentLanguage.id]?.id;
  }

  createSynopsis() {
    const currentLanguageId = this.currentLanguage.id;
    const title = this.formGroup.get([
      'synopsis.' + currentLanguageId + '.title',
    ]).value;
    const synopsis = this.formGroup.get([
      'synopsis.' + currentLanguageId + '.synopsis',
    ]).value;
    this.store.dispatch(
      createSynopsis({
        postId: this.post.id,
        languageId: this.currentLanguage.id,
        title: title,
        synopsis: synopsis,
        queryId: '',
      }),
    );
  }
  updateSynopsis() {
    const currentLanguageId = this.currentLanguage.id;
    const title = this.formGroup.get([
      'synopsis.' + currentLanguageId + '.title',
    ]).value;
    const synopsis = this.formGroup.get([
      'synopsis.' + currentLanguageId + '.synopsis',
    ]).value;
    this.store.dispatch(
      updateSynopsis({
        postId: this.post.id,
        id: this.currentSynopsisId,
        title: title,
        synopsis: synopsis,
      }),
    );
  }

  aiGenerateSynopsis() {
    this.store.dispatch(
      generateAISynopsis({
        postId: this.post.id,
        languageId: this.currentLanguage.id,
      }),
    );
  }

  copyFromOriginal() {
    this.formGroup.patchValue({
      ['synopsis.' + this.currentLanguage.id + '.title']: this.post.title,
      ['synopsis.' + this.currentLanguage.id + '.synopsis']: this.post.content,
    });
  }

  copyFromTranslation() {
    if (this.post.type !== 'social') {
      this.formGroup.patchValue({
        ['synopsis.' + this.currentLanguage.id + '.title']:
          this.post.translation?.[this.currentLanguage.id]?.title,
      });
    }
    this.formGroup.patchValue({
      ['synopsis.' + this.currentLanguage.id + '.synopsis']:
        this.post.translation?.[this.currentLanguage.id]?.content,
    });
  }

  deleteSynopsis() {
    this.store.dispatch(
      deleteSynopsis({
        postId: this.post.id,
        id: this.currentSynopsisId,
      }),
    );
  }

  onLanguageChanged(language: Language) {
    this.currentLanguage = language;
    this.estimateHasSynopsisLanguage();
  }
}
